import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useUserContext } from '../../context/UserContext';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { MobileNav } from './MobileNav';
import { LoadingSpinner } from '../LoadingSpinner';
import { getPageKeyFromPath, ROUTES } from '../../routes';
import { isAdmin } from '../../lib/roles';
import type { NavigationItem } from './types';
import type { Store } from '../../api';

export type PageKey =
  | 'complaints'
  | 'submit-complaint'
  | 'analytics'
  | 'stores'
  | 'admin'
  | 'team'
  | 'settings';

// Map page keys to routes
const PAGE_KEY_TO_ROUTE: Record<PageKey, string> = {
  complaints: ROUTES.COMPLAINTS,
  'submit-complaint': ROUTES.SUBMIT_COMPLAINT,
  analytics: ROUTES.ANALYTICS,
  stores: ROUTES.STORES,
  admin: ROUTES.ADMIN,
  team: ROUTES.VENDOR_ADMIN,
  settings: ROUTES.SETTINGS,
};

interface AppShellProps {
  children: React.ReactNode;
  stores?: Store[];
}

export function AppShell({ children, stores: _stores = [] }: AppShellProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, brandId, environment, authMethod, triggerOnboarding, isMAppContext } =
    useUserContext();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Get current page from route
  const currentPage = getPageKeyFromPath(location.pathname) as PageKey;

  // Navigate to a page using the router
  const handleNavigate = (pageKey: PageKey) => {
    const route = PAGE_KEY_TO_ROUTE[pageKey];
    if (route) {
      navigate(route);
    }
  };

  const hasAdminAccess = isAdmin(user?.roles);
  const isVendor = user?.userType === 'vendor';
  const isVendorAdmin = isVendor && user?.vendorRole === 'admin';
  const canSubmitComplaints = !isVendor; // Only internal users (store managers) can submit

  // Build navigation items based on user role
  // Note: Dashboard has been merged into Complaints page
  const navigationItems: NavigationItem[] = [
    {
      key: 'complaints',
      label: 'Complaints',
      icon: 'clipboard-list',
      path: ROUTES.COMPLAINTS,
      description: isVendor ? 'View assigned complaints' : 'View and manage complaints',
    },
    ...(canSubmitComplaints
      ? [
          {
            key: 'submit-complaint' as PageKey,
            label: 'Submit Complaint',
            icon: 'plus-circle' as const,
            path: ROUTES.SUBMIT_COMPLAINT,
            description: 'File a new quality complaint',
          },
        ]
      : []),
    ...(!isVendor
      ? [
          {
            key: 'analytics' as PageKey,
            label: 'Analytics',
            icon: 'chart-bar' as const,
            path: ROUTES.ANALYTICS,
            description: 'View complaint analytics and trends',
          },
        ]
      : []),
    // {
    //   key: 'stores',
    //   label: 'Restaurants',
    //   icon: 'building-storefront',
    //   path: ROUTES.STORES,
    //   description: 'View accessible restaurants',
    // },
    ...(hasAdminAccess
      ? [
          {
            key: 'admin' as PageKey,
            label: 'Admin',
            icon: 'cog' as const,
            path: ROUTES.ADMIN,
            description: 'Vendor management',
            separator: true,
          },
        ]
      : []),
    ...(isVendorAdmin
      ? [
          {
            key: 'team' as PageKey,
            label: 'Team Management',
            icon: 'users' as const,
            path: ROUTES.VENDOR_ADMIN,
            description: 'Manage your team members',
            separator: true,
          },
        ]
      : []),
    {
      key: 'settings',
      label: 'Settings',
      icon: 'user-circle',
      path: ROUTES.SETTINGS,
      description: 'Profile and preferences',
    },
  ];

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="lg" message="Loading..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar (desktop only) */}
      <Sidebar
        navigationItems={navigationItems}
        currentPage={currentPage}
        onNavigate={handleNavigate}
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
      />

      {/* Main content area */}
      <div
        className={`flex flex-col min-h-screen transition-all duration-300 ${
          sidebarCollapsed ? 'lg:pl-20' : 'lg:pl-64'
        }`}
      >
        {/* Header */}
        <Header
          user={user}
          authMethod={authMethod}
          environment={environment}
          brandId={brandId}
          onLogout={logout}
          onSettingsClick={() => handleNavigate('settings')}
          onAppTourClick={authMethod === 'mapp' ? triggerOnboarding : undefined}
          showLogout={!isMAppContext}
          currentPageLabel={navigationItems.find((item) => item.key === currentPage)?.label || ''}
        />

        {/* Page content */}
        <main className="flex-1 px-4 pt-4 sm:px-6 sm:pt-6 lg:px-8 lg:pt-8 pb-24 lg:pb-8">{children}</main>
      </div>

      {/* Mobile bottom navigation */}
      <MobileNav
        navigationItems={navigationItems}
        currentPage={currentPage}
        onNavigate={handleNavigate}
      />
    </div>
  );
}
